import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';

const api = axios.create({ baseURL: '' });

api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('mv_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401) {
      const url = err.config?.url || '';
      // /auth/me is the bootstrap auth-check — App.js handles its 401 gracefully.
      // Redirecting here would race with the React render and cause a double-logout loop.
      if (!url.includes('/auth/me')) {
        localStorage.removeItem('mv_token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export { api };

const useStore = create(
  persist(
    (set, get) => ({
  user: null,
  settings: {},

  // ── Email campaign state ────────────────────────────────────────────────────
  campaign: null,

  startCampaign: (data) => set({
    campaign: {
      ...data,
      id: `camp_${Date.now()}`,
      index: 0,
      sent: 0,
      failed: 0,
      running: true,
      failedItems: [],
      saved: false,
    },
  }),

  pauseCampaign: () => set(s => ({
    campaign: s.campaign ? { ...s.campaign, running: false } : null,
  })),

  resumeCampaign: () => set(s => ({
    campaign: s.campaign ? { ...s.campaign, running: true } : null,
  })),

  stopCampaign: async () => {
    const { campaign } = get();
    if (campaign && !campaign.saved && campaign.index > 0) {
      try {
        await api.post('/api/campaigns', {
          accountId: campaign.accountId,
          accountEmail: campaign.accountEmail,
          subject: campaign.subject,
          total: campaign.total,
          sent: campaign.sent,
          failed: campaign.failed,
          failedItems: campaign.failedItems,
          status: 'stopped',
        });
      } catch {}
    }
    set({ campaign: null });
  },

  advanceCampaign: async (success, emailAddr, error) => {
    const prev = get().campaign;
    if (!prev) return;
    const newIndex = prev.index + 1;
    const failedItems = success
      ? prev.failedItems
      : [...prev.failedItems, { email: emailAddr, error: error || 'Failed' }];
    const isDone = newIndex >= prev.total;
    const newSent = prev.sent + (success ? 1 : 0);
    const newFailed = prev.failed + (success ? 0 : 1);
    set({
      campaign: {
        ...prev,
        index: newIndex,
        sent: newSent,
        failed: newFailed,
        failedItems,
        running: isDone ? false : prev.running,
        saved: isDone ? true : prev.saved,
      },
    });
    if (isDone) {
      try {
        await api.post('/api/campaigns', {
          accountId: prev.accountId,
          accountEmail: prev.accountEmail,
          subject: prev.subject,
          total: prev.total,
          sent: newSent,
          failed: newFailed,
          failedItems,
          status: 'completed',
        });
      } catch {}
    }
  },

  login: async (username, password) => {
    const form = new URLSearchParams({ username, password });
    const { data } = await api.post('/auth/login', form, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    localStorage.setItem('mv_token', data.access_token);
    set({ user: { username: data.username, role: data.role } });
    return data;
  },

  logout: () => {
    localStorage.removeItem('mv_token');
    set({ user: null });
    window.location.href = '/login';
  },

  fetchMe: async () => {
    try {
      const { data } = await api.get('/auth/me');
      set({ user: data });
      return data;
    } catch {
      return null;
    }
  },

  fetchSettings: async () => {
    const { data } = await api.get('/api/settings');
    set({ settings: data });
    return data;
  },

  updateSettings: async (updates) => {
    const { data } = await api.put('/api/settings', updates);
    set({ settings: data });
    return data;
  },
    }),
    {
      name: 'classmail-campaign',
      partialize: (state) => ({ campaign: state.campaign }),
    }
  )
);

export default useStore;
